import { useState } from "react";
import {
  EinvoiceTransactionListModel,
  EinvoiceTransactionSearchParamsModel,
} from "../../models/einvoiceTransaction/einvoiceTransactionModel";
import { searchEnvoiceTransaction } from "../../serviceBroker/envoiceTransactionServiceBroker";

// Interface for data state
interface EinvoiceTransactionState {
  data: EinvoiceTransactionListModel[];
  loading: boolean;
  totalCount: number;
}

const DEFAULT_PAGE_SIZE = 10;

export const useEinvoiceTransaction = (
  initialParams: EinvoiceTransactionSearchParamsModel = {}
) => {
  // Split into two separate state hooks
  const [searchParams, setSearchParams] =
    useState<EinvoiceTransactionSearchParamsModel>({
      ...initialParams,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });

  const [dataState, setDataState] = useState<EinvoiceTransactionState>({
    data: [],
    loading: false,
    totalCount: 0,
  });

  // Fetch data function
  const fetchData = async (params = searchParams) => {
    setDataState((current) => ({ ...current, loading: true }));

    try {
      const result = await searchEnvoiceTransaction(params);

      setDataState((current) => ({
        ...current,
        data: result?.Result || [],
        totalCount: result?.TotalRecoredCount || 0,
        loading: false,
      }));
      return result;
    } catch (error) {
      setDataState((current) => ({ ...current, loading: false }));
      return null;
    }
  };

  // Update search param
  const updateSearchParam = (key: string, value: any) => {
    setSearchParams((current) => ({
      ...current,
      [key]: value,
    }));
  };

  // Handle page change
  const onCurrentPageChange = (pageNumber: number) => {
    setSearchParams((current) => ({
      ...current,
      pageNumber,
    }));

    fetchData({
      ...searchParams,
      pageNumber,
    });
  };

  // Handle page size change
  const onPageSizeChange = (pageSize: number) => {
    setSearchParams((current) => ({
      ...current,
      pageSize,
      pageNumber: 1, // Reset to first page when changing page size
    }));

    fetchData({
      ...searchParams,
      pageSize,
      pageNumber: 1,
    });
  };

  // Reset search
  const resetSearch = () => {
    const resetParams = {
      pageNumber: 1,
      pageSize: searchParams.pageSize || DEFAULT_PAGE_SIZE,
    };

    setSearchParams(resetParams);

    return resetParams;
  };

  return {
    data: dataState.data,
    loading: dataState.loading,
    searchParams,
    totalCount: dataState.totalCount,

    // Actions
    fetchData,
    updateSearchParam,
    resetSearch,
    onCurrentPageChange,
    onPageSizeChange,
  };
};
